import MojitoPresentation from 'mojito/presentation';
import MojitoCore from 'mojito/core';
import { isEmpty, invert, mapValues, castArray, isNil } from 'mojito/utils';
import { createPath, parsePath } from 'history';
import { exec } from 'preact-iso/router';

const AbstractRouteResolver = MojitoCore.Base.AbstractRouteResolver;
const { NAVIGATION, PARAMETER } = MojitoPresentation.Base.Navigation.types;
const { BAD_REQUEST_PAGE } = NAVIGATION;
const log = MojitoCore.logger.get('RouteResolver');
const isStrictPattern = route => !route.endsWith('/*') && !route.endsWith('/:rest*');
const convertToStrict = route =>
    isStrictPattern(route) ? route : route.replace(/\/(:\w+)?\*$/, '');

/**
 *
 * RouteResolver constructor.
 *
 * @classdesc Default route resolver for resolving logical content uri's to concrete routes.
 *
 * @class RouteResolver
 * @param {object} paths - Paths map object. Typically key in this map is one of the {@link Mojito.Presentation.Base.Navigation.types|navigation type}
 * and value is path patter, e.g., '/inplay/events/:eventId'.
 * @param {string} [root = '/'] - The root of all route paths. For example, if root: '/sportsbook' is specified then
 * all navigation will be performed under '/sportsbook' root prefix, e.g., '/sportsbook/inplay/events/:eventId'.
 * @param {boolean|Array<string>} [persistQueryParams = true] - Accepts boolean and array of param names. If true then all query params will be persistent during navigation, otherwise query param will not be persistent. If array of string is provided then only query params with names included in this array will be persistent.
 *
 * @extends Mojito.Core.Base.AbstractRouteResolver
 * @memberof Mojito.Applications.Common
 */
export default class RouteResolver extends AbstractRouteResolver {
    constructor(paths = {}, root = '/', persistQueryParams = true) {
        super();
        this.paths = paths;
        this.root = root;
        this.stringResolver = undefined;
        this.persistQueryParams = persistQueryParams;
    }

    setRoot(value) {
        this.root = value;
    }

    getRoot() {
        return this.root;
    }

    setStringResolver(stringResolver) {
        this.stringResolver = stringResolver;
    }

    getStringResolver() {
        return this.stringResolver;
    }

    getPattern(type) {
        if (!type) {
            log.warn(`Type pattern is not provided`);
        }
        const pattern = this.paths[type];
        if (typeof pattern !== 'string' && typeof pattern !== 'object' && !Array.isArray(pattern)) {
            return;
        }
        return this.enrichPatternWithRoot(pattern);
    }

    enrichPatternWithRoot(pattern) {
        if (typeof pattern === 'string' || Array.isArray(pattern)) {
            return this.enrichWithRoot(pattern);
        }
        if (typeof pattern === 'object') {
            const patternCopy = { ...pattern };
            if (Object.hasOwn(patternCopy, 'path')) {
                patternCopy.path = this.enrichWithRoot(pattern.path);
            }
            return patternCopy;
        }
        return pattern;
    }

    decodeParam(name, value) {
        const stringResolver = this.getStringResolver();
        if (!stringResolver) {
            return value;
        }
        const paramKey = invert(PARAMETER)[name];
        const l10nNode = `$PATH_PARAMS.${paramKey}`;
        const paramTranslations = this.stringResolver.resolveString(l10nNode, true);
        return invert(paramTranslations)[value];
    }

    encodeParam(name, value) {
        const stringResolver = this.getStringResolver();
        if (!stringResolver) {
            return value;
        }
        const paramKey = invert(PARAMETER)[name];
        const l10nKey = `$PATH_PARAMS.${paramKey}.${value}`;
        return stringResolver.resolveString(l10nKey, true) || value;
    }

    ensureNavigationParamsEncoding(navigationPayload) {
        if (!navigationPayload) {
            return;
        }
        const { params = {} } = navigationPayload;
        const encodedParams = mapValues(params, (value, key) => this.encodeParam(key, value));
        return { ...navigationPayload, params: encodedParams };
    }

    /**
     * Resolves navigation route to be used in URL.
     * If NavigationPayload type is not provided or not found in paths, then {@link Mojito.Presentation.Base.Navigation.types|BAD_REQUEST_PAGE}
     * route will be generated.
     *
     * @param {Mojito.Presentation.Base.Navigation.types.NavigationPayload} navigationPayload - Navigation payload object.
     * @param {boolean} [useAllPaths = false] - This parameter has only effect for navigations with multiple path setup. If true, then this function will return an array of all path items configured in 'paths' map object for a specific navigation type. Otherwise, it will return string that contains the first path from an array.
     * Note: if navigation type {@link Mojito.Presentation.Base.Navigation.types|CUSTOM_NAVIGATION} is used then the route from 'url' property will be used to generate function result.
     *
     * @returns {string|Array<string>|undefined} Result of {@link RouteResolver#resolveRoutePath} after {@link RouteResolver#ensureQueryParams}.
     * @function Mojito.Applications.Common.RouteResolver#getRoute
     */
    getRoute(navigationPayload, useAllPaths = false) {
        return this.ensureQueryParams(this.resolveRoutePath(navigationPayload, useAllPaths));
    }

    /**
     * Resolves navigation payload to a concrete path (with root prefix), without merging persisted query params.
     * See {@link RouteResolver#getRoute} for the full URL used in navigation.
     *
     * @param {Mojito.Presentation.Base.Navigation.types.NavigationPayload} navigationPayload - Navigation payload object.
     * @param {boolean} [useAllPaths = false] - Same as {@link RouteResolver#getRoute}.
     * @returns {string|Array<string>|undefined} Resolved path(s).
     * @function Mojito.Applications.Common.RouteResolver#resolveRoutePath
     * @private
     */
    resolveRoutePath(navigationPayload, useAllPaths = false) {
        const { type } = navigationPayload;
        navigationPayload = this.ensureNavigationParamsEncoding(navigationPayload);
        if (type === NAVIGATION.CUSTOM_NAVIGATION) {
            return this.enrichWithRoot(navigationPayload.params[PARAMETER.URL]);
        }
        const hasPattern = !!this.getPattern(type);

        if (!hasPattern) {
            return this.enrichWithRoot(this.getPattern(BAD_REQUEST_PAGE));
        }
        const pattern = this.resolveNestedRoute(navigationPayload, this.paths, useAllPaths);
        return this.enrichWithRoot(pattern);
    }

    /**
     * This method recursively traverses through all the nested navigation types and resolves them into a single absolute route.
     *
     * @param {Mojito.Presentation.Base.Navigation.types.NavigationPayload} navigationPayload - Navigation payload object.
     * @param {object} paths - An object mapping paths, where each key typically corresponds to a {@link Mojito.Presentation.Base.Navigation.types|navigation type}.
     * @param {boolean} useAllPaths - This parameter has only effect for navigations with multiple path setup. If true, then this function will return an array of all path items configured in 'paths' map object for a specific navigation type. Otherwise, it will return string that contains the first path from an array.
     * @returns {string|Array|undefined} If successful, the method returns a string or an array of strings representing the fully resolved navigation route in the format 'path/params/nestedRoute', otherwise, it returns 'undefined' if the route cannot be resolved.
     * @private
     */
    resolveNestedRoute(navigationPayload, paths, useAllPaths) {
        if (isEmpty(paths) || isEmpty(navigationPayload)) {
            return;
        }
        const { type, params, anchor, nested } = navigationPayload;
        let nestedRoute;
        const pattern = paths[type];
        let path = pattern;
        // path will be always undefined because custom navigation doesn't exist in paths
        if (type === NAVIGATION.CUSTOM_NAVIGATION) {
            path = params[PARAMETER.URL];
        }

        const adaptResponse = routes => (useAllPaths && routes.length > 1 ? routes : routes[0]);

        if (path.nested) {
            const nestedNavigation = this.ensureNavigationParamsEncoding(nested);
            nestedRoute = this.resolveNestedRoute(nestedNavigation, pattern.nested, useAllPaths);
            path = nestedRoute
                ? castArray(nestedRoute).flatMap(nestedPathItem => {
                      return castArray(pattern.path)
                          .map(convertToStrict)
                          .map(pathItem => {
                              const route = [
                                  RouteResolver.generatePath(pathItem, params),
                                  nestedPathItem,
                              ]
                                  .filter(Boolean)
                                  .join('/');
                              return route.replace(/\/+/g, '/');
                          });
                  })
                : castArray(pattern.path)
                      .map(pathItem => RouteResolver.generatePath(pathItem, params))
                      .filter(pathItem => !isNil(pathItem));

            return adaptResponse(path);
        }

        const pathRoutes = castArray(path)
            .map(pathItem => {
                const anchorLink = anchor ? `#${anchor}` : '';
                pathItem = RouteResolver.generatePath(pathItem, params);
                return !isNil(pathItem) ? `${pathItem}${anchorLink}` : undefined;
            })
            .filter(Boolean);

        return adaptResponse(pathRoutes);
    }

    /**
     * Enrich navigation path with root prefix.
     *
     * @param {string} path - Navigation path.
     *
     * @returns {string} Navigation path prefixed with root.
     * @function Mojito.Applications.Common.RouteResolver#enrichWithRoot
     */
    enrichWithRoot(path) {
        if (Array.isArray(path)) {
            return path.map(pathSegment => this.enrichPathSegmentWithRoot(pathSegment));
        }
        return this.enrichPathSegmentWithRoot(path);
    }

    /**
     * Appends query parameters from the current URL (`window.location.search`) onto the given route,
     * according to {@link RouteResolver#persistQueryParams}: `false` returns `path` unchanged; `true` keeps
     * all current params; a string array keeps only those param names. Params already present on `path`
     * are not overwritten.
     *
     * @param {string|Array<string>} path - Route path, optionally with an existing `?query` (arrays are mapped per segment).
     * @returns {string|Array<string>} Path with persisted query string applied.
     * @function Mojito.Applications.Common.RouteResolver#ensureQueryParams
     */
    ensureQueryParams(path) {
        if (Array.isArray(path)) {
            return path.map(segment => this.ensureQueryParams(segment));
        }
        const isEmptyParams = params => Array.isArray(params) && params.length === 0;
        if (!this.persistQueryParams || isEmptyParams(this.persistQueryParams)) {
            return path;
        }
        const paramsToPersist = this.createSearchParamsToPersist();
        if (!paramsToPersist) {
            return path;
        }

        const { pathname, search = '', hash = '' } = parsePath(path);
        const resultingParams = new URLSearchParams(search || '');

        for (const [key, value] of paramsToPersist.entries()) {
            if (!resultingParams.has(key)) {
                resultingParams.append(key, value);
            }
        }

        return createPath({
            pathname,
            search: resultingParams.toString(),
            hash,
        });
    }

    createSearchParamsToPersist() {
        const currentQueryParams = new URLSearchParams(window.location.search);
        const shouldPersistAll = this.persistQueryParams === true;
        if (shouldPersistAll) {
            return currentQueryParams;
        }
        if (Array.isArray(this.persistQueryParams)) {
            const paramsObj = new URLSearchParams();
            this.persistQueryParams.forEach(name => {
                currentQueryParams.getAll(name).forEach(value => paramsObj.append(name, value));
            });
            return paramsObj;
        }
    }

    enrichPathSegmentWithRoot(pathSegment) {
        // Note: if path starts with forward slash, we assume it is relative to the domain name (absolute path), hence to root will be attached e.g. "/register" will ignore the root
        if (pathSegment?.startsWith('/')) {
            return pathSegment;
        }
        let root = this.getRoot() || '/';
        if (isEmpty(pathSegment)) {
            return root;
        }
        if (root !== '/') {
            root = `${root}/`;
        }
        // Prevent slashes duplication
        return `${root}${pathSegment}`.replace(/\/+/g, '/');
    }

    /**
     * Detects if navigation payload pattern matches on a location path.
     *
     * @param {Mojito.Presentation.Base.Navigation.types.NavigationPayload} navigationData - Navigation payload object.
     * @param {{path: string, query: object, route: Function}} location - Location object returned by preact-iso useLocation() hook.
     * @param {boolean} [strict = false] - Either match should be strict.
     *
     * @returns {boolean} True if location path matches mojito navigation payload.
     * @function Mojito.Applications.Common.RouteResolver#isMatchLocation
     */
    isMatchLocation(navigationData, location, strict = false) {
        const routePath = this.resolveRoutePath(navigationData, true);
        const shouldStrictMatch = strict || navigationData.type === NAVIGATION.HOME;
        const getMatchPath = path => (shouldStrictMatch ? path : `${path}/:rest*`);

        const routes = castArray(routePath).filter(route =>
            shouldStrictMatch ? isStrictPattern(route) : true
        );
        return routes.some(path => {
            const matchPath = getMatchPath(path);
            return !!exec(location.path, matchPath);
        });
    }

    /**
     * Substitutes `:param` placeholders in a route pattern with corresponding values from the params object.
     *
     * @param {string} pattern - Route pattern containing `:param` placeholders, e.g., '/sport/:sportId/matches/:filter'.
     * @param {object} [params = {}] - Key-value map where keys match placeholder names and values are their replacements.
     *
     * @returns {string|undefined} Route path with all placeholders replaced by param values.
     * @static
     * @function Mojito.Applications.Common.RouteResolver.generatePath
     */
    static generatePath(pattern, params = {}) {
        if (
            isStrictPattern(pattern) &&
            Object.keys(params).some(key => !isNil(params[key]) && !pattern.includes(`:${key}`))
        ) {
            return;
        }
        return RouteResolver.enrichPatternWithParams(pattern, params);
    }

    /**
     * Replaces `:param` segments in a route pattern with the corresponding values from `params`.
     * Optional placeholders (`:param?`) are omitted when the value is missing; duplicate slashes are collapsed.
     * Example: `account/:itemId?/:partId?` with params `{ itemId: 'bet-history', partId: '338525' }` → `account/bet-history/338525`;
     * `account/:itemId?/:partId?` with params `{ itemId: 'bet-history' }` → `account/bet-history`.
     *
     * @param {string} pattern - Route pattern, e.g. `account/:itemId?/:partId?`.
     * @param {object} [params = {}] - Param values keyed by placeholder name.
     * @returns {string} Concrete route path.
     * @private
     * @static
     */
    static enrichPatternWithParams(pattern, params = {}) {
        const path = pattern.replace(/:([^/]+)/g, (match, rawKey) => {
            const paramKey = rawKey.replace(/[+*?]$/, '');
            return isNil(params[paramKey]) ? '' : encodeURIComponent(params[paramKey]);
        });
        return path.replace(/\/+/g, '/').replace(/\/$/, '');
    }
}
